import React, { useEffect, useState, useContext } from "react";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Box,
  MenuItem,
  TextField,
  Chip,
  Pagination,
} from "@mui/material";
import { Link } from "react-router-dom";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import StarIcon from "@mui/icons-material/Star";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import axios from "../services/axiosInstance";
import { UserContext } from "../context/UserContext";
import API_BASE_URL from "../../src/config";

const Events = () => {
  const { user } = useContext(UserContext);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [event_type, setEventType] = useState("");
  const [event_fees, setEventFees] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest"); // Renamed to 'sort' for backend compatibility
  const [page, setPage] = useState(1);
  const [totalEvents, setTotalEvents] = useState(0);
  const [userCity, setUserCity] = useState("");

  const eventTypes = ["Online", "In-Person", "Hybrid"];
  const sortOptions = [
    { label: "Newest", value: "newest" },
    { label: "Popular", value: "popular" },
    { label: "Date Ascending", value: "date_asc" },
    { label: "Price Low to High", value: "price_low_to_high" },
    { label: "Price High to Low", value: "price_high_to_low" },
  ];

  // Fetch user's interested city (if logged in)
  useEffect(() => {
    if (user?.id) {
      fetchUserCity(user.id);
    } else {
      fetchEvents(true); // Show all events for guest users
    }
  }, [user]);

  const fetchUserCity = async (userId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/users/${userId}`);
      const city = response.data.interested_city;
      if (city) {
        setUserCity(city);
      } else {
        fetchEvents(true);
      }
    } catch (error) {
      console.error("Failed to fetch user city:", error);
      fetchEvents(true);
    }
  };

  // Re-fetch events on filter changes
  useEffect(() => {
    if (userCity) {
      fetchEvents(false); // Filter by city
    } else {
      fetchEvents(true); // Show all
    }
  }, [page, event_type, event_fees, sort, search, userCity]); // Using 'sort' instead of 'sortBy'

  const fetchEvents = async (ignoreCity = false) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/events`, {
        params: {
          limit: 1000,
          sort: sort, // Updated to use 'sort' instead of 'sortBy'
          search,
          event_type,
          event_fees, // Handle event fees correctly
        },
      });

      const allEvents = response.data.events || [];

      const filteredEvents =
        ignoreCity || !userCity
          ? allEvents
          : allEvents.filter(
              (event) =>
                event.location &&
                event.location
                  .trim()
                  .toLowerCase()
                  .includes(userCity.trim().toLowerCase())
            );

      const paginatedEvents = filteredEvents.slice((page - 1) * 6, page * 6);

      setEvents(paginatedEvents);
      setTotalEvents(filteredEvents.length);
    } catch (err) {
      console.error("🔥 Error fetching events:", err);
      setError("Failed to load events. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 5, mb: 3 }}>
      <Typography
        variant="h4"
        sx={{ color: "#42326e" }}
        fontWeight="bold"
        textAlign="center"
        mb={3}
      >
        Upcoming Events
      </Typography>

      {userCity && (
        <Typography variant="subtitle1" textAlign="center" mb={2}>
          Showing events in{" "}
          <strong style={{ color: "#42326e" }}>{userCity}</strong>
        </Typography>
      )}

      <Typography variant="body1" textAlign="center" sx={{ mb: 4 }}>
        Explore networking events, workshops, and conferences near you.
      </Typography>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 5, mb: 3, flexWrap: "wrap" }}>
        <TextField
          label="Search Events...."
          variant="outlined"
          size="medium"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 250 }}
        />
        <TextField
          select
          label="Event Type"
          variant="outlined"
          size="medium"
          value={event_type}
          onChange={(e) => setEventType(e.target.value)}
          sx={{ width: 250 }}
        >
          {eventTypes.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Event Fees"
          variant="outlined"
          size="medium"
          value={event_fees}
          onChange={(e) => setEventFees(e.target.value)}
          sx={{ width: 250 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="Free">Free</MenuItem>
          <MenuItem value="Paid">Paid</MenuItem>
        </TextField>
        <TextField
          select
          label="Sort By"
          variant="outlined"
          size="medium"
          value={sort}
          onChange={(e) => setSort(e.target.value)} // Using 'sort' instead of 'sortBy'
          sx={{ width: 250 }}
        >
          {sortOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {loading ? (
        <Box sx={{ textAlign: "center", mt: 3 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" textAlign="center">
          {error}
        </Typography>
      ) : (
        <>
          <Grid container spacing={3}>
            {events.length > 0 ? (
              events.map((event) => (
                <Grid item xs={12} sm={6} md={4} key={event.id}>
                  <Card
                    sx={{
                      p: 2,
                      boxShadow: 3,
                      position: "relative",
                      bgcolor: event.featured
                        ? "rgba(255, 223, 186, 0.3)"
                        : "white",
                    }}
                  >
                    <CardContent>
                      {event.featured && (
                        <Chip
                          label="Featured"
                          color="secondary"
                          icon={<StarIcon />}
                          sx={{ position: "absolute", top: 10, right: 10 }}
                        />
                      )}
                      <Typography variant="h6" fontWeight="bold">
                        {event.name}
                      </Typography>
                      <Box display="flex" alignItems="center" mt={1}>
                        <CalendarTodayIcon color="action" sx={{ mr: 1 }} />
                        <Typography variant="body2">
                          {new Date(event.event_date).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            }
                          )}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" mt={1}>
                        <LocationOnIcon color="action" sx={{ mr: 1 }} />
                        <Typography variant="body2">{event.location}</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" mt={1}>
                        <CurrencyRupeeIcon color="action" sx={{ mr: 1 }} />
                        <Typography variant="body2">
                          {event.event_fees > 0
                            ? `${event.event_fees}`
                            : "Free Event"}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" mt={1}>
                        <EventAvailableIcon color="action" sx={{ mr: 1 }} />
                        <Typography variant="body2">{event.event_type}</Typography>
                      </Box>

                      <Button
                        variant="contained"
                        sx={{
                          mt: 2,
                          width: "100%",
                          backgroundColor: "#42326e",
                          color: "white",
                          "&:hover": {
                            backgroundColor: "black",
                            color: "white",
                          },
                        }}
                        component={Link}
                        to={`/event/${event.id}`}
                        startIcon={<EventAvailableIcon />}
                        disabled={!user}
                      >
                        {user ? "View Details" : "Please Login to View"}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            ) : (
              <Typography
                variant="body1"
                textAlign="center"
                sx={{ width: "100%", mt: 3 }}
              >
                No upcoming events at the moment.
              </Typography>
            )}
          </Grid>

          <Box display="flex" justifyContent="center" mt={4}>
            <Pagination
              count={Math.ceil(totalEvents / 6)}
              page={page}
              onChange={(event, value) => setPage(value)}
              color="primary"
            />
          </Box>
        </>
      )}
    </Container>
  );
};

export default Events;
